import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";

interface User {
  id: string;
  email: string;
  displayName: string | null;
  role: "SUPER_ADMIN" | "ORG_ADMIN" | "MEMBER";
}

interface Props {
  organizationId: string;   // 目标公司(只能给本公司的 MEMBER 授权)
  adAccountId: string;
  adAccountName: string;
  onClose: () => void;
}

type GrantType = "PERMANENT" | "TEMPORARY";

export default function GrantDialog({ organizationId, adAccountId, adAccountName, onClose }: Props) {
  const qc = useQueryClient();
  const [userId, setUserId] = useState("");
  const [grantType, setGrantType] = useState<GrantType>("PERMANENT");
  const [expiresAt, setExpiresAt] = useState(""); // datetime-local string
  const [reason, setReason] = useState("");

  const users = useQuery({
    queryKey: ["users", organizationId],
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/v1/users",
        { params: { query: { organizationId } as never } } as never
      );
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as User[];
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("请选择用户");
      const body: { userId: string; grantType: GrantType; expiresAt?: string; reason?: string } = {
        userId, grantType,
      };
      if (grantType === "TEMPORARY") {
        if (!expiresAt) throw new Error("临时授权必须填过期时间");
        body.expiresAt = new Date(expiresAt).toISOString();
      }
      if (reason.trim()) body.reason = reason.trim();
      const { error, response } = await api.POST(
        "/api/v1/ad-accounts/{accountId}/grants",
        { params: { path: { accountId: adAccountId } }, body }
      );
      if (error || !response.ok) throw error || new Error("授权失败");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grants", adAccountId] });
      toast.success("已授权");
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-background rounded-lg border border-border w-full max-w-md shadow-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="font-semibold">授权用户访问</h2>
            <div className="text-xs text-muted-foreground truncate">{adAccountName}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
          className="p-5 space-y-4 text-sm"
        >
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">选择用户</span>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">{users.isLoading ? "加载中…" : "选择本公司用户"}</option>
              {users.data?.map((u) => (
                <option key={u.id} value={u.id}>
                  {(u.displayName ?? u.email)} · {u.role}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={grantType === "PERMANENT"}
                onChange={() => setGrantType("PERMANENT")}
              />
              永久
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={grantType === "TEMPORARY"}
                onChange={() => setGrantType("TEMPORARY")}
              />
              临时(带过期时间)
            </label>
          </div>

          {grantType === "TEMPORARY" && (
            <label className="block space-y-1.5">
              <span className="text-xs font-medium">过期时间</span>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
          )}

          <label className="block space-y-1.5">
            <span className="text-xs font-medium">备注(可选)</span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="例:协助 Q2 推广活动"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {create.isPending ? "授权中…" : "确认授权"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
